import React, { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { BsPlayFill, BsPauseFill, BsStopFill } from "react-icons/bs";
import { encodeAudioBufferToMp3 } from "./convertToMp3";
import "./css/EditStemModal.css";

const loadAudioSafely = async (audioUrl, wavesurfer) => {
  const controller = new AbortController();

  try {
    const response = await fetch(audioUrl, { signal: controller.signal });
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    wavesurfer.load(objectUrl);

    return () => {
      controller.abort();
      URL.revokeObjectURL(objectUrl);
    };
  } catch (error) {
    if (error.name !== "AbortError") {
      console.error("Error loading audio:", error);
    }

    return () => controller.abort();
  }
};

const createWaveSurfer = (container, color) =>
  WaveSurfer.create({
    container,
    waveColor: "#444",
    progressColor: color || "#0ff",
    height: 60,
    barWidth: 2,
    responsive: true,
  });

const safeFileName = (value = "stem") =>
  value
    .toString()
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .replace(/_+/g, "_");

const createAudioBufferFromStem = (stem, sampleRate) => {
  const length = Math.min(stem.left.length, stem.right.length);
  const audioContext = new AudioContext({ sampleRate });

  const buffer = audioContext.createBuffer(2, length, sampleRate);
  buffer.copyToChannel(stem.left.slice(0, length), 0);
  buffer.copyToChannel(stem.right.slice(0, length), 1);

  return {
    audioContext,
    buffer,
  };
};

const stemToMp3File = async ({ stem, sampleRate, fileName }) => {
  const { audioContext, buffer } = createAudioBufferFromStem(stem, sampleRate);

  const mp3Data = await encodeAudioBufferToMp3(buffer, 128);

  await audioContext.close?.();

  const mp3Blob =
    mp3Data instanceof Blob
      ? mp3Data
      : new Blob([mp3Data], { type: "audio/mpeg" });

  const file = new File([mp3Blob], fileName, {
    type: "audio/mpeg",
  });

  const url = URL.createObjectURL(mp3Blob);

  return {
    file,
    url,
  };
};

const StemPlayer = ({ stem, generated = false }) => {
  const waveformRef = useRef(null);
  const waveSurfer = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);

  useEffect(() => {
    let cleanup = () => {};

    if (waveformRef.current && stem.url) {
      waveSurfer.current = createWaveSurfer(
        waveformRef.current,
        generated ? "#facc15" : "#0ff",
      );

      loadAudioSafely(stem.url, waveSurfer.current).then(
        (cleanupFn) => (cleanup = cleanupFn),
      );

      waveSurfer.current.setVolume(volume);
    }

    return () => {
      cleanup();
      waveSurfer.current?.destroy();
    };
  }, [stem.url, generated]);

  useEffect(() => {
    waveSurfer.current?.setVolume(volume);
  }, [volume]);

  const togglePlay = () => {
    waveSurfer.current?.playPause();
    setIsPlaying(waveSurfer.current?.isPlaying());
  };

  const stop = () => {
    waveSurfer.current?.stop();
    setIsPlaying(false);
  };

  const handleVolume = (e) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
  };

  return (
    <div className={generated ? "stem-player generated-stem" : "stem-player"}>
      <div className="stem-header">
        <h4>
          {stem.name}
          {generated ? " - Generated MP3" : ""}
        </h4>

        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={handleVolume}
          className="volume-slider"
        />
      </div>

      <div ref={waveformRef} className="waveform" />

      <div className="player-controls">
        <button onClick={togglePlay}>
          {isPlaying ? <BsPauseFill /> : <BsPlayFill />}
        </button>

        <button onClick={stop}>
          <BsStopFill />
        </button>

        {generated && stem.url ? (
          <a
            className="download-generated-stem"
            href={stem.url}
            download={stem.file?.name}
          >
            Download
          </a>
        ) : null}
      </div>
    </div>
  );
};

const MainBeatPlayer = ({
  url,
  beatName,
  autoLoading,
  autoStatus,
  autoProgress,
  onAutoCreate,
}) => {
  const waveformRef = useRef(null);
  const waveSurfer = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);

  useEffect(() => {
    let cleanup = () => {};

    if (waveformRef.current && url) {
      waveSurfer.current = createWaveSurfer(waveformRef.current, "#ff0066");

      loadAudioSafely(url, waveSurfer.current).then(
        (cleanupFn) => (cleanup = cleanupFn),
      );

      waveSurfer.current.setVolume(volume);
    }

    return () => {
      cleanup();
      waveSurfer.current?.destroy();
    };
  }, [url]);

  useEffect(() => {
    waveSurfer.current?.setVolume(volume);
  }, [volume]);

  const togglePlay = () => {
    waveSurfer.current?.playPause();
    setIsPlaying(waveSurfer.current?.isPlaying());
  };

  const stop = () => {
    waveSurfer.current?.stop();
    setIsPlaying(false);
  };

  const handleVolume = (e) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
  };

  return (
    <div className="main-beat-player">
      <div className="main-beat-header">
        <h4>{beatName}</h4>

        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={handleVolume}
          className="volume-slider"
        />
      </div>

      <div ref={waveformRef} className="waveform" />

      <div className="player-controls">
        <button onClick={togglePlay}>
          {isPlaying ? <BsPauseFill /> : <BsPlayFill />}
        </button>

        <button onClick={stop}>
          <BsStopFill />
        </button>

        <button
          className="auto-create-stems-btn"
          onClick={onAutoCreate}
          disabled={autoLoading || !url}
        >
          {autoLoading ? "Creating..." : "Auto Create"}
        </button>
      </div>

      {autoStatus ? (
        <div className="auto-stem-status">
          {autoStatus}
          {autoProgress > 0 ? ` - ${autoProgress}%` : ""}
        </div>
      ) : null}
    </div>
  );
};

const EditStemModal = ({
  stems = [],
  mainBeatFile,
  beatName,
  artistName = "Unknown",
  onUploadAutoStems,
  onClose,
}) => {
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoStatus, setAutoStatus] = useState("");
  const [autoProgress, setAutoProgress] = useState(0);
  const [generatedStems, setGeneratedStems] = useState([]);
  const [uploadingGenerated, setUploadingGenerated] = useState(false);
  useEffect(() => {
    if (!mainBeatFile && (!stems || stems.length === 0)) return;

    console.log("🎧 Main beat audio link:", mainBeatFile);

    console.log(
      "🎛️ Existing stems:",
      stems.map((stem) => ({
        name: stem.name,
        url: stem.url,
        path: stem.path,
      })),
    );
  }, [mainBeatFile, stems]);
  useEffect(() => {
    return () => {
      generatedStems.forEach((stem) => {
        if (stem.url?.startsWith("blob:")) {
          URL.revokeObjectURL(stem.url);
        }
      });
    };
  }, [generatedStems]);

  const autoCreateStems = async () => {
    if (!mainBeatFile) {
      setAutoStatus("Main beat audio file is missing.");
      return;
    }

    setAutoLoading(true);
    setAutoStatus("Preparing browser Demucs...");
    setAutoProgress(0);

    generatedStems.forEach((stem) => {
      if (stem.url?.startsWith("blob:")) {
        URL.revokeObjectURL(stem.url);
      }
    });

    setGeneratedStems([]);

    try {
      const [{ DemucsProcessor, CONSTANTS }, ort] = await Promise.all([
        import("demucs-web"),
        import("onnxruntime-web"),
      ]);

      const isMobileDevice =
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        window.innerWidth <= 768;

      if (ort.env?.wasm) {
        ort.env.wasm.numThreads = isMobileDevice
          ? 1
          : Math.min(navigator.hardwareConcurrency || 4, 4);
      }

      let executionProviders = ["wasm"];

      // Do NOT use WebGPU on mobile.
      // Poco/Android WebGPU can fail with GPUDevice createBuffer errors.
      if (!isMobileDevice && "gpu" in navigator) {
        try {
          const adapter = await navigator.gpu.requestAdapter();

          if (adapter) {
            executionProviders = ["webgpu", "wasm"];
            ort.env.webgpu = {
              powerPreference: "high-performance",
            };
          }
        } catch {
          executionProviders = ["wasm"];
        }
      }

      setAutoStatus(
        executionProviders.includes("webgpu")
          ? "Using desktop WebGPU engine..."
          : "Using stable WASM engine...",
      );

      const processor = new DemucsProcessor({
        ort,
        sessionOptions: {
          executionProviders,
          enableCpuMemArena: false,
          enableMemPattern: false,
          graphOptimizationLevel: "basic",
        },
        onDownloadProgress: (loaded, total) => {
          if (!total) return;

          const percent = Math.round((loaded / total) * 100);
          setAutoProgress(percent);
          setAutoStatus("Downloading Demucs model");
        },
        onProgress: ({ progress, currentSegment, totalSegments }) => {
          const percent = Math.round(progress * 100);
          setAutoProgress(percent);
          setAutoStatus(`Separating stems ${currentSegment}/${totalSegments}`);
        },
        onLog: (phase, message) => {
          console.log(`[${phase}] ${message}`);
        },
      });

      setAutoStatus("Loading model...");
      await processor.loadModel(CONSTANTS.DEFAULT_MODEL_URL);

      setAutoStatus("Fetching main beat audio...");
      const response = await fetch(mainBeatFile);
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();

      setAutoStatus("Decoding audio...");
      const audioContext = new AudioContext({
        sampleRate: CONSTANTS.SAMPLE_RATE,
      });

      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const leftChannel = audioBuffer.getChannelData(0);
      const rightChannel =
        audioBuffer.numberOfChannels > 1
          ? audioBuffer.getChannelData(1)
          : leftChannel;

      setAutoStatus("Running Demucs in browser...");
      const result = await processor.separate(leftChannel, rightChannel);

      await audioContext.close();

      const beatPart = safeFileName(beatName || "beat");
      const artistPart = safeFileName(artistName || "artist");
      const baseName = `${beatPart}_${artistPart}`;

      setAutoStatus("Converting generated stems to MP3...");

      const drumsMp3 = await stemToMp3File({
        stem: result.drums,
        sampleRate: CONSTANTS.SAMPLE_RATE,
        fileName: `${baseName}_drums.mp3`,
      });

      const bassMp3 = await stemToMp3File({
        stem: result.bass,
        sampleRate: CONSTANTS.SAMPLE_RATE,
        fileName: `${baseName}_bass.mp3`,
      });

      const instrumentMp3 = await stemToMp3File({
        stem: result.other,
        sampleRate: CONSTANTS.SAMPLE_RATE,
        fileName: `${baseName}_instrument.mp3`,
      });

      const createdStems = [
        {
          key: "drums",
          name: "Drums",
          file: drumsMp3.file,
          url: drumsMp3.url,
        },
        {
          key: "bass",
          name: "Bass",
          file: bassMp3.file,
          url: bassMp3.url,
        },
        {
          key: "instrument",
          name: "Instrument",
          file: instrumentMp3.file,
          url: instrumentMp3.url,
        },
      ];

      console.log(
        "🤖 Auto-created stem links:",
        createdStems.map((stem) => ({
          name: stem.name,
          fileName: stem.file?.name,
          url: stem.url,
        })),
      );

      setGeneratedStems(createdStems);

      setAutoProgress(100);
      setAutoStatus("Auto-created stems ready. Click Upload Stems to save.");
    } catch (error) {
      console.error(error);
      setAutoStatus(
        error?.message ||
          "Auto stem creation failed. Try Chrome/Edge and a shorter audio file.",
      );
    } finally {
      setAutoLoading(false);
    }
  };

  const uploadGeneratedStems = async () => {
    if (!generatedStems.length) {
      setAutoStatus("No generated stems to upload.");
      return;
    }

    if (!onUploadAutoStems) {
      setAutoStatus("Upload handler missing.");
      return;
    }

    setUploadingGenerated(true);
    setAutoStatus("Uploading generated stems...");

    try {
      await onUploadAutoStems(generatedStems);
      setAutoStatus("Generated stems uploaded and saved successfully.");
    } catch (error) {
      console.error(error);
      setAutoStatus(error?.message || "Generated stems upload failed.");
    } finally {
      setUploadingGenerated(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="edit-stem-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stem-modal-header">
          <h3>Stems Preview</h3>

          <button className="close-btn" onClick={onClose}>
            X
          </button>
        </div>

        <div className="stem-list">
          {stems.map((stem, i) => (
            <StemPlayer key={`${stem.name}-${i}`} stem={stem} />
          ))}
        </div>

        {mainBeatFile && (
          <div className="main-beat-section">
            <MainBeatPlayer
              url={mainBeatFile}
              beatName={beatName}
              autoLoading={autoLoading}
              autoStatus={autoStatus}
              autoProgress={autoProgress}
              onAutoCreate={autoCreateStems}
            />
          </div>
        )}

        {generatedStems.length > 0 ? (
          <div className="generated-stems-section">
            <h3>Auto Created Stems</h3>

            <div className="stem-list">
              {generatedStems.map((stem) => (
                <StemPlayer key={stem.key} stem={stem} generated />
              ))}
            </div>

            <button
              className="upload-generated-stems-btn"
              onClick={uploadGeneratedStems}
              disabled={uploadingGenerated}
            >
              {uploadingGenerated ? "Uploading..." : "Upload Stems"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EditStemModal;
